import { ReactNode } from 'react';
import classNames from 'classnames';
import { Stack } from '@tiendanube/components';
import ScrollProvider from 'App/components/AppLayout/ScrollProvider';
import useIonPageStratus from './useIonPageStratus';
import './IonPageStratus.scss';

export interface IonPageStratusProps {
  // This is the content of the page
  children: ReactNode;
  // This is Header is for mobile view
  headerTop?: ReactNode;
  // This is Header is for desktop view
  headerContent?: ReactNode;
  // This indicates if the header must be fixed on desktop view into topbar space
  headerFixed?: boolean;
  width?: 'small' | 'medium' | 'large';
}

function IonPageStratus({
  headerTop,
  headerContent,
  headerFixed = false,
  children,
  width = 'large',
}: Readonly<IonPageStratusProps>) {
  const {
    refFixedHeader,
    refContent,
    isMobile,
    isShowTopbar,
    height: heightTopBar,
    mustRenderFixedHeader,
  } = useIonPageStratus({
    hasHeaderTop: headerTop !== undefined,
    hasHeaderFixed: headerFixed,
  });

  const classStratusPageContent = classNames(
    'stratus--page-content-container',
    `stratus--page-width-${width}`,
    { 'stratus--page-no-header-top': headerTop === undefined && isMobile },
  );

  const OFF_SET = 16;
  const DIVIDER_OFF_SET = 24;

  const heightFixedHeader = refFixedHeader.current?.offsetHeight || 0;

  const renderHeaderContentAsFixed = (
    <>
      <div
        ref={refFixedHeader}
        className="stratus--fixed-header"
        style={{
          transform: `translateY(${
            isShowTopbar ? 0 : -(heightTopBar + OFF_SET) // This moves the header to the topbar location.
          }px)`,
        }}
      >
        {headerContent}
      </div>
      <div
        className="stratus--fixed-header-divider"
        style={{
          opacity: isShowTopbar ? 0 : 1,
          top: heightFixedHeader - DIVIDER_OFF_SET, // This fixs the divider under the fixed header.
        }}
      />
    </>
  );

  const renderHeaderContentAsUnfixed = headerContent;

  return (
    <div className="stratus--page">
      {!!headerTop && headerTop}
      <ScrollProvider>
        {mustRenderFixedHeader && renderHeaderContentAsFixed}
        <div ref={refContent} className={classStratusPageContent}>
          {!mustRenderFixedHeader && renderHeaderContentAsUnfixed}
          <div
            className="stratus--page-content"
            style={{
              ...(mustRenderFixedHeader && {
                marginTop: heightFixedHeader - OFF_SET,
              }), // This add space to the content to avoid overlap with the fixed header.
            }}
          >
            <Stack column align="stretch" spacing="base">
              {children}
            </Stack>
          </div>
        </div>
      </ScrollProvider>
    </div>
  );
}

export default IonPageStratus;
